import type { ReactNode } from "react";
import { Link } from "react-router";
import { Botao, Chip } from "../ui";
import { classes } from "../../nucleo/classes";
import { MarcaGeovendas } from "./MarcaGeovendas";
import { ControleDeTema } from "./ControleDeTema";

export interface UsuarioNaTopbar {
  nome: string;
  papel: string;
}

export interface PropsDaTopbar {
  /** Nome da área — "Portal de Implantação" no lado do cliente, "Console" no interno. */
  area: string;
  /** Ambiente informado pela API (`dev`, `staging`, `prod`). Ausente enquanto não responde. */
  ambiente?: string;
  interno?: boolean;
  usuario?: UsuarioNaTopbar;
  /** Quando informado, a topbar oferece encerrar a sessão. */
  aoSair?: () => void;
  /** Quando informado, a topbar leva à segurança da conta (segundo fator e sessões abertas). */
  segurancaPara?: string;
  /** A pesquisa do console: mora no cabeçalho, entre a marca e o usuário. */
  busca?: ReactNode;
  /** O sino de notificações. Vem de fora para a barra continuar sem conhecer a API. */
  sino?: ReactNode;
  className?: string;
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const primeira = partes[0]?.[0] ?? "";
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? "") : "";
  return (primeira + ultima).toUpperCase() || "?";
}

export function Topbar({
  area,
  ambiente,
  interno = false,
  usuario,
  aoSair,
  segurancaPara,
  busca,
  sino,
  className,
}: PropsDaTopbar) {
  return (
    <header className={classes("topbar", className)}>
      <div className="flex items-center gap-3 flex-wrap">
        <MarcaGeovendas area={area} />
        <span className={classes("selo", interno && "selo--interno")}>
          {interno ? "Interno" : "Cliente"}
        </span>
        {/* Sem resposta da API o ambiente é desconhecido — e é isso que a tela diz, em vez
            de assumir "dev" e esconder que ninguém sabe onde a página está rodando. */}
        <span className="selo" data-testid="selo-ambiente">
          {ambiente ?? "ambiente ?"}
        </span>
      </div>

      {busca ? <div className="topbar__busca">{busca}</div> : null}

      <div className="flex items-center gap-4 flex-wrap">
        {sino}
        <ControleDeTema />
        {usuario ? (
          <div className="quem" data-testid="quem-esta-dentro">
            <span>
              {usuario.nome} · {usuario.papel}
            </span>
            <span className="avatar" aria-hidden="true">
              {iniciais(usuario.nome)}
            </span>
            {segurancaPara ? (
              <Link
                to={segurancaPara}
                className="botao botao--fantasma botao--pequeno"
                data-testid="ir-para-a-seguranca"
              >
                Segurança
              </Link>
            ) : null}
            {aoSair ? (
              <Botao variante="fantasma" tamanho="pequeno" onClick={aoSair}>
                Sair
              </Botao>
            ) : null}
          </div>
        ) : (
          <Chip estado="neutro" data-testid="estado-sessao">
            Sem sessão
          </Chip>
        )}
      </div>
    </header>
  );
}
